import { useState } from "react";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";

import numbers from "core/numbers";
import dates from "core/dates";
import useToggle from "core/hooks/useToggle";

import Box from "ui/atom/Box";
import Icon from "ui/atom/Icon";
import Button from "ui/base/Button";
import Image from "ui/base/Image";

import placeholder from "assets/placeholder.svg";
import pass from "assets/pass.svg"; /* need to replace to a 3:2 version (maybe with a clock icon in it) */


const Actions = ({ content, refund }) => {
	const theme = useTheme();
	const gift = content?.gift;

	const text = () => {
		if (gift) {
			if (gift.isReceived) return "Gift received";
			return "Gift sent";
		}
		else return "Unlocked";
	};

	const style = {
		actions: { justifyContent: "space-between", alignItems: "center" },
		skeleton: { backgroundColor: theme.palette.article.skeleton },
		action: { backgroundColor: theme.palette.article.skeleton, m: 1 }
	};

	if (!content) return <Actions.Skeleton style={style} />;
	return (
		<Box sx={style.actions}>
			<Typography color="text.secondary"><b>{text()}</b> on {dates.brief(content.purchasedOn)}</Typography>
			<Box gap={0.5}>
				{gift?.canRefund && <Button.Confirmation icon="ticket-x" title="Refund Gift" text="The gift will be canceled and can’t be redeemed. You’ll receive a refund for the gift cost." color={theme.palette.text.primary} loading={refund.isPending} onClick={() => refund.mutate(content?.gift?.id)} size={20} />}
				<Button.Icon to={`/gift?${(gift?.id && !gift?.isReceived) ? `sub_gid=${gift?.id}` : `sub_cid=${content.id}`}`} icon="gift" color={theme.palette.text.primary} size={20} />
				{/* <Button.Icon icon="eye-off" color="text.primary" size={20} /> todo: add hide feature add*/}
				<Button.Clipboard color={theme.palette.text.primary} size={20} value={content.url} title="Copy URL" />
				<Button.Share color={theme.palette.text.primary} size={20} title="Subly" text={content.title} url={content.url} />
			</Box>
		</Box>
	);
};

Actions.Skeleton = ({ style }) => {
	return (
		<Box sx={style.actions}>
			<Skeleton variant="text" width={180} sx={style.skeleton} />
			<Box gap={0.5}>
				<Skeleton variant="rounded" width={20} height={20} sx={style.action} />
				<Skeleton variant="rounded" width={20} height={20} sx={style.action} />
				<Skeleton variant="rounded" width={20} height={20} sx={style.action} />
				<Skeleton variant="rounded" width={20} height={20} sx={style.action} />
			</Box>
		</Box>
	);
};

const Card = ({ children, image, tokens, skeleton, isGift, isCampaign, ...props }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(t => t.breakpoints.down("sm"));
	const size = isMobile ? 100 : 130;

	const style = {
		root: {
			position: "relative",
			alignItems: "center",
			width: "100%",
			mx: "auto",
			mb: 1,
			backgroundColor: "background.white",
			borderRadius: "8px",
			boxShadow: `0 0 10px 0 ${theme.palette.article.shadow}`,
			color: "text.black",
			overflow: "hidden",
			transition: "all 500ms ease",
			cursor: "pointer",
			":hover": {
				transform: "scale(1.03)",
				boxShadow: `0 0 20px 0 ${theme.palette.article.shadow}`
			}
		},
		image: {
			m: 1,
			borderRadius: "12px"
		},
		chip: {
			position: "absolute", top: 8, right: 8,
			height: "18px",
			p: theme.spacing(0, 0, 0, 0.5),
			".MuiChip-label": {
				p: theme.spacing(0, 1, 0, 0.5)
			}
		},
		content: {
			flex: "1 1 auto",
			alignSelf: "flex-start",
			height: "100%",
			textAlign: "left",
			p: theme.spacing(1.5, 1.5, 1.5, 0)
		},
		skeleton: { backgroundColor: theme.palette.article.skeleton }
	};

	if (skeleton) return <Card.Skeleton style={style} />;
	return (
		<Box gap={1} {...props} sx={{ ...style.root, ...props.sx }}>
			<Image.Cover src={image} fallback={placeholder} size={size} color={theme.palette.article.skeleton} sx={style.image} />
			{tokens && <Chip variant="coin" icon={<Icon name={isCampaign ? "campaign-coin" : "coin"} size={12} />} label={numbers.format(tokens, "number")} sx={style.chip} />}
			<Box.Column gap={1} sx={style.content}>
				{children}
			</Box.Column>
		</Box>
	);
};

Card.Skeleton = ({ style }) => {
	const isMobile = useMediaQuery(t => t.breakpoints.down("sm"));
	const height = isMobile ? 100 : 130;

	return (
		<Box gap={1} sx={style.root}>
			<Skeleton variant="rectangular" sx={{ ...style.image, ...style.skeleton, aspectRatio: "3/2", m: 1, p: 0 }} height={height} />
			<Skeleton variant="rounded" sx={{ ...style.chip, ...style.skeleton }} width={35} />
			<Box.Column gap={1.25} sx={style.content}>
				<Typography variant="subtitle2" color="Typography.black">
					<Skeleton width={50} sx={style.skeleton} />
				</Typography>
				<Skeleton variant="rectangular" sx={{ ...style.logo, ...style.skeleton }} />
				<Typography variant="body3" fontWeight={700} color="Typography.black">
					<Skeleton width="100%" sx={style.skeleton} />
					<Skeleton width="100%" sx={style.skeleton} />
				</Typography>
			</Box.Column>
		</Box>
	);
};

Content.Logo = ({ src }) => {
	const theme = useTheme();
	const style = {
		logo: { width: "120px", height: "auto" }
	};

	return <Image src={src} color={theme.palette.article.skeleton} sx={style.logo} />;
};

const Empty = () => {
	const theme = useTheme();
	return (
		<Box.Column gap={2} sx={{ alignItems: "center" }}>
			<Icon name="clock-alert" size={30} color={theme.palette.text.disabled} />
			<Typography variant="h6" color="textDisabled">No articles visited</Typography>
		</Box.Column>
	);
};

Content.Time = ({ content, time, article }) => {
	const [open, setOpen] = useState(false);

	const contentTime = content.time;
	const contents = content?.time?.contents;
	const diff = dates.hours.difference(contentTime.endOn);
	const expired = diff < 0;

	const style = {
		date: { mr: 5 },
		content: {
			justifyContent: "space-between"
		},
		expiration: {
			display: "flex",
			flexWrap: "wrap",
			gap: 0.25,
			alignItems: "center"
		},
		action: {
			p: 0.5, mt: "auto",
			alignSelf: "flex-end"
		},
		contents: {
			borderLeft: "2px solid #c5c5c5",
			ml: 2.5,
			pl: 2.5
		}
	};

	const handleContents = async () => {
		if (!open && !contents) time.mutate(contentTime.id);
		setOpen(s => !s);
	};

	return (
		<Box.Column gap={2}>
			<Card image={pass} tokens={content.pass?.tokens} onClick={handleContents} skeleton={!content}>
				<Typography variant="body4" color="text.black" sx={style.date}>
					{dates.monthDay(contentTime.startOn)} to {dates.brief(contentTime.endOn)}
				</Typography>
				<Box.Column gap={0.5}>
					<Content.Logo src={content.partner.logo.dark} />
					{expired
						? <Chip variant="filled" color="tertiary" label="Expired" fontWeight={700} size="small" sx={{ width: "75px" }} />
						: (
							<Typography variant="body3" fontWeight={700} component="span" sx={style.expiration}>
								Expires in:
								<Typography variant="body3" color="tertiary" component="span" sx={style.expiration}>
									<Icon name="timer" /> {dates.hours.dynamic(diff)?.value}
								</Typography>
							</Typography>
						)}
				</Box.Column>
				<Button loading={time.isPending} endIcon={<Icon name={open ? "chevron-up" : "chevron-down"} />} sx={style.action}>Pass History</Button>
			</Card>
			{open && (
				<>
					{contents?.length ?
						(
							<Box.Column gap={1} sx={style.contents}>
								{contents.map((c, k) => <Content.Article content={c} article={article} chip={false} key={k} />)}
							</Box.Column>
						)
						: <Empty />
					}
				</>
			)}
		</Box.Column>
	);
};

Content.Article = ({ content, article, chip = true }) => {
	const [loading, toggle] = useToggle();

	const style = {
		title: {
			display: "-webkit-box",
			overflow: "hidden",
			WebkitLineClamp: "3",
			WebkitBoxOrient: "vertical"
		},
		card: {
			cursor: loading ? "progress" : undefined,
			animation: loading ? "pulse 1.2s ease-in-out infinite" : undefined,
			"@keyframes pulse": {
				"0%, 100%": { opacity: 1 },
				"50%": { opacity: 0.4 }
			}
		}
	};

	const click = async (e) => {
		if (article.isPending) return;

		const tab = e.button === 1 || (e.button === 0 && e.ctrlKey); // middle click or ctrl + left click
		toggle();
		await article.mutateAsync({ contentId: content?.id, tab });
		setTimeout(() => toggle(), 1000);
	};

	const preventScroll = (e) => e.button === 1 && e.preventDefault();

	return (
		<Card
			image={content?.thumbUrl}
			tokens={chip && content?.tokens}
			isGift={Boolean(content?.gift)}
			onClick={click}
			onAuxClick={click}
			onMouseDown={preventScroll}
			isCampaign={content?.isCampaign}
			skeleton={!content}
			sx={style.card}
		>
			<Typography variant="body4" color="text.black">
				{content?.publishedOn && dates.brief(content?.publishedOn)}
			</Typography>
			<Box.Column gap={0.5}>
				<Content.Logo src={content?.partner.logo.dark} />
				<Typography variant="body3" fontWeight={700} color="text.black" sx={style.title}>{content?.title}</Typography>
				{content?.isCampaign && <Chip variant="filled" color="secondary" label="Campaign" fontWeight={700} size="small" sx={{ width: "80px" }} />}
			</Box.Column>
		</Card>
	);
};

export default function Content({ content, time, article, refund }) {
	const isPass = Boolean(content?.time);

	return (
		<Box.Column gap={0.5}>
			<Actions content={content} refund={refund} />
			{isPass
				? <Content.Time content={content} time={time} article={article} />
				: <Content.Article content={content} article={article} />
			}
		</Box.Column>
	);
};